import type { KpiWeeklyEntry } from "../../types/kpiDashboard";

// ── Parse helpers ─────────────────────────────────────────────────────────────

/**
 * Normalises a localised number string to a JS-parseable float string.
 * Handles both European (1.234,56) and English (1,234.56) formats:
 *   - If the last separator is a comma → European: dots=thousands, comma=decimal
 *   - If only dots exist and the final dot has exactly 3 trailing digits → thousands separator
 *   - Otherwise → English: commas=thousands, dot=decimal
 */
const normalizeNumber = (v: string): string => {
  const s = v.replace(/[€%\s\u00A0]/g, '').trim();
  const lastComma = s.lastIndexOf(',');
  const lastDot   = s.lastIndexOf('.');

  if (lastComma > lastDot) {
    // European format: "1.234,56" or "123,45"
    return s.replace(/\./g, '').replace(',', '.');
  } else if (lastDot !== -1 && lastComma === -1 && s.slice(lastDot + 1).length === 3) {
    // Round European thousands: "1.000" → "1000"
    return s.replace(/\./g, '');
  } else {
    // English format or plain decimal: remove thousands commas
    return s.replace(/,/g, '');
  }
};

export const parsePct   = (v?: string) => v ? parseFloat(normalizeNumber(v)) || 0 : 0;
export const parseNum   = (v?: string) => v ? parseFloat(normalizeNumber(v)) || 0 : 0;
export const parseEuro  = (v?: string) => v ? parseFloat(normalizeNumber(v)) || 0 : 0;
export const parseHours = (v?: string) => v ? parseInt(v.split(':')[0]) || 0 : 0;

/** Parse "07/11/2025, 4:56:56 PM" → { year, month } */
export const parseDateTime = (v?: string): { year: number; month: number } | null => {
  if (!v) return null;
  const m = v.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? { year: parseInt(m[3]), month: parseInt(m[2]) } : null;
};

// ── Mapper ────────────────────────────────────────────────────────────────────

export interface RawSheetsData {
  stationRows:    string[][];
  anomalyRows:    string[][];
  invoiceSummary: string[][];
  overdueByWeek:  Map<number, Array<{ docNumber: string; company: string; amount: number; daysOverdue: number }>>;
  failures:       Array<{ station: string; year: string; month: string; duration: string; hours: number }>;
  cplRows:        string[][]; // each row: [year, label, Jan, Feb, …, Dec]
}

/**
 * Maps raw sheet data into a KpiWeeklyEntry array.
 * Pure transformation — no API calls, no side effects.
 */
export function mapKpiDataFromSheets(rawData: RawSheetsData): KpiWeeklyEntry[] {
  const { stationRows, anomalyRows, invoiceSummary, overdueByWeek, failures, cplRows } = rawData;

  // Anomaly: KW → counts (current year only)
  const anomalyByWeek = new Map<number, { meterStop: number; partnerLeer: number; noConsumption: number; total: number }>();
  for (const r of anomalyRows) {
    const kw = parseInt(r[0]);
    if (!kw) continue;
    const meterStop     = parseInt(r[1]) || 0;
    const partnerLeer   = parseInt(r[2]) || 0;
    const noConsumption = parseInt(r[3]) || 0;
    const total         = parseInt(r[4]) || 0;
    if (meterStop + partnerLeer + noConsumption > 0) {
      anomalyByWeek.set(kw, { meterStop, partnerLeer, noConsumption, total });
    }
  }

  // Invoice totals per week
  const invoiceByWeek = new Map<number, number>();
  for (const r of invoiceSummary) {
    const kw = parseInt(r[0]);
    if (kw && r[1]) invoiceByWeek.set(kw, parseEuro(r[1]));
  }

  // Marketing: cplRows are tagged [year, label, Jan…Dec].
  // Build per-year Ads Cost arrays.
  const adsCostByYear = new Map<number, string[]>();
  for (const r of cplRows) {
    if (r[1] === "Ads Cost") {
      adsCostByYear.set(parseInt(r[0]), r.slice(2)); // index 0 = Jan … 11 = Dec
    }
  }

  const CURRENT_YEAR  = new Date().getFullYear();
  const CURRENT_MONTH = new Date().getMonth() + 1; // 1-indexed

  /** Returns the last month index (1-indexed) that has a non-zero cost. */
  const lastNonZeroMonth = (costs: string[]): number => {
    for (let i = costs.length - 1; i >= 0; i--) {
      if (parseEuro(costs[i]) > 0) return i + 1;
    }
    return costs.length;
  };

  const entries: KpiWeeklyEntry[] = stationRows
    .filter(r => r[0] && r[1] && parseInt(r[0]) >= 2025)
    .map(r => {
      const year = parseInt(r[0]);
      const week = parseInt(r[1]);
      return {
        year,
        week,
        stations: {
          active:           parseInt(r[3])  || 0,
          inactive:         parseInt(r[4])  || 0,
          total:            parseInt(r[5])  || 0,
          onlinePct:        parsePct(r[6]),
          energyAc:         parseNum(r[8]),
          energyDc:         parseNum(r[9]),
          energyTotal:      parseNum(r[10]),
          energyGrowthPct:  parsePct(r[13]),
          sessionsAc:       parseNum(r[14]),
          sessionsDc:       parseNum(r[15]),
          sessionsTotal:    parseNum(r[16]),
          // BOF-76: the sessions block is O:AC P:DC Q:Total R:Free S:Daily T:Growth — indices
          // 14..19. These three used to be off by one, so the free-session count was shown as
          // "daily sessions" and the daily count was rendered as a growth percentage.
          freeSessions:     parseNum(r[17]),
          dailySessions:    parseNum(r[18]),
          sessionGrowthPct: parsePct(r[19]),
          stationGrowthPct: parsePct(r[7]),
        },
        anomaly:         year === CURRENT_YEAR ? (anomalyByWeek.get(week) ?? null) : null,
        invoice:         invoiceByWeek.has(week) ? { amount: invoiceByWeek.get(week)! } : null,
        overdueInvoices: year === CURRENT_YEAR ? (overdueByWeek.get(week) ?? null) : null,
        failures:        null,
        marketing:       null,
      };
    });

  // Attach failures to the most recent entry
  if (entries.length > 0) {
    entries[entries.length - 1].failures = failures.length ? failures : null;
  }

  // Attach marketing to the last entry of each year so getKpiForPeriod can
  // look up the correct year's data when a past period is selected.
  const entryYears = [...new Set(entries.map(e => e.year))];
  for (const year of entryYears) {
    const monthCosts = adsCostByYear.get(year);
    if (!monthCosts) continue;

    const month             = year === CURRENT_YEAR ? CURRENT_MONTH : lastNonZeroMonth(monthCosts);
    const adsCostThisMonth  = parseEuro(monthCosts[month - 1]);
    const adsCostPrevMonth  = month > 1 ? parseEuro(monthCosts[month - 2]) : 0;
    const totalMarketingCost = monthCosts.reduce((sum, v) => sum + parseEuro(v), 0);

    if (totalMarketingCost > 0) {
      const lastOfYear = entries.filter(e => e.year === year).pop()!;
      lastOfYear.marketing = { adsCostThisMonth, adsCostPrevMonth, totalMarketingCost };
    }
  }

  return entries;
}
